/**
 * ConfigurationItem entity adapter (P15.3 / #416).
 *
 * Quirks: no `code` column (CIs have no stable slug -- they're CMDB
 * rows). Default sort + search are name-based. `linked_service_id`
 * is a free-text input here at v0.15.0 (a service-picker would mirror
 * the parentSelect pattern; backlog #531 once a customer asks).
 */
import { makeTwinDrillSection } from "./TwinDrillSection";
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export interface ConfigurationItem {
  id: string;
  name: string;
  ci_class: string | null;
  linked_service_id: string | null;
  // P117.5: SAMA-mandated CI asset-register fields.
  asset_owner: string | null;
  asset_custodian: string | null;
  physical_location: string | null;
  logical_location: string | null;
  pci_scope: boolean | null;
  backup: string | null;
  primary_process: string | null;
  secondary_process: string | null;
  contract_ref: string | null;
  vendor_contact: string | null;
  vendor_sla: string | null;
  // P136 (P117f.4): Tier-1 implied-SSOT CMDB fields.
  asset_display_name: string | null;
  ip_address: string | null;
  install_status: string | null;
  environment: string | null;
  support_group: string | null;
  manufacturer: string | null;
  model_name: string | null;
  serial_number: string | null;
  asset_model_category: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConfigurationItemIn {
  name: string;
  ci_class: string | null;
  linked_service_id: string | null;
  asset_owner: string | null;
  asset_custodian: string | null;
  physical_location: string | null;
  logical_location: string | null;
  pci_scope: boolean;
  backup: string | null;
  primary_process: string | null;
  secondary_process: string | null;
  contract_ref: string | null;
  vendor_contact: string | null;
  vendor_sla: string | null;
  asset_display_name: string | null;
  ip_address: string | null;
  install_status: string | null;
  environment: string | null;
  support_group: string | null;
  manufacturer: string | null;
  model_name: string | null;
  serial_number: string | null;
  asset_model_category: string | null;
}

export type ConfigurationItemPatch = Partial<ConfigurationItemIn>;

const columns: AdminColumn<ConfigurationItem>[] = [
  {
    key: "name",
    label: "Name",
    sortValue: (r) => r.name,
    render: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
  },
  {
    key: "ci_class",
    label: "Class",
    sortValue: (r) => r.ci_class ?? "",
    render: (r) =>
      r.ci_class ? (
        <span
          data-testid="ci-class-chip"
          className="inline-flex items-center rounded-full bg-slate-100 text-slate-800 px-2 py-0.5 text-xs"
        >
          {r.ci_class}
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "linked_service_id",
    label: "Linked service",
    render: (r) =>
      r.linked_service_id ? (
        <span
          data-testid="ci-linked-service-cell"
          className="font-mono text-xs text-slate-500"
          title={r.linked_service_id}
        >
          {r.linked_service_id.slice(0, 8)}...
        </span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const SAMA = "SAMA asset register";
const CMDB = "CMDB details";

const formFields: FormField<ConfigurationItemIn>[] = [
  // Core identity (ungrouped -- renders flat at the top).
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  { kind: "text", key: "ci_class", label: "CMDB class", maxLength: 64 },
  // Service picker would mirror parentSelect; deferred to backlog #531.
  { kind: "text", key: "linked_service_id", label: "Linked service id (UUID)" },
  // SAMA-mandated asset-register fields (P117.5).
  {
    kind: "text",
    key: "asset_owner",
    label: "Asset owner",
    maxLength: 512,
    group: SAMA,
  },
  {
    kind: "text",
    key: "asset_custodian",
    label: "Asset custodian",
    maxLength: 512,
    group: SAMA,
  },
  {
    kind: "text",
    key: "physical_location",
    label: "Physical location",
    maxLength: 512,
    group: SAMA,
  },
  {
    kind: "text",
    key: "logical_location",
    label: "Logical location",
    maxLength: 512,
    group: SAMA,
  },
  { kind: "bool", key: "pci_scope", label: "PCI scope", group: SAMA },
  { kind: "text", key: "backup", label: "Backup", maxLength: 256, group: SAMA },
  {
    kind: "text",
    key: "primary_process",
    label: "Primary process",
    maxLength: 512,
    group: SAMA,
  },
  {
    kind: "text",
    key: "secondary_process",
    label: "Secondary process",
    maxLength: 512,
    group: SAMA,
  },
  {
    kind: "text",
    key: "contract_ref",
    label: "Contract ref",
    maxLength: 256,
    group: SAMA,
  },
  {
    kind: "text",
    key: "vendor_contact",
    label: "Vendor contact",
    maxLength: 512,
    group: SAMA,
  },
  { kind: "text", key: "vendor_sla", label: "Vendor SLA", maxLength: 512, group: SAMA },
  // Tier-1 implied-SSOT CMDB fields (P136 / P117f.4).
  {
    kind: "text",
    key: "asset_display_name",
    label: "Asset display name",
    maxLength: 255,
    group: CMDB,
  },
  { kind: "text", key: "ip_address", label: "IP address", maxLength: 64, group: CMDB },
  {
    kind: "text",
    key: "install_status",
    label: "Install status",
    maxLength: 64,
    group: CMDB,
  },
  {
    kind: "text",
    key: "environment",
    label: "Environment",
    maxLength: 32,
    group: CMDB,
  },
  {
    kind: "text",
    key: "support_group",
    label: "Support group",
    maxLength: 255,
    group: CMDB,
  },
  {
    kind: "text",
    key: "manufacturer",
    label: "Manufacturer",
    maxLength: 255,
    group: CMDB,
  },
  { kind: "text", key: "model_name", label: "Model name", maxLength: 255, group: CMDB },
  {
    kind: "text",
    key: "serial_number",
    label: "Serial number",
    maxLength: 255,
    group: CMDB,
  },
  {
    kind: "text",
    key: "asset_model_category",
    label: "Asset model category",
    maxLength: 128,
    group: CMDB,
  },
];

export const configurationItemAdapter: EntityAdapter<
  ConfigurationItem,
  ConfigurationItemIn,
  ConfigurationItemPatch
> = {
  i18nPrefix: "modules.foundation.admin.configurationItems",
  importKind: "configuration_item",
  endpoint: "/v1/admin/foundation/configuration-items",
  queryKey: ["foundation-admin", "configuration-items"] as const,
  columns,
  defaultSort: { key: "name", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.name} ${r.ci_class ?? ""}`,
  formFields,
  formInitial: () => ({
    name: "",
    ci_class: null,
    linked_service_id: null,
    asset_owner: null,
    asset_custodian: null,
    physical_location: null,
    logical_location: null,
    pci_scope: false,
    backup: null,
    primary_process: null,
    secondary_process: null,
    contract_ref: null,
    vendor_contact: null,
    vendor_sla: null,
    asset_display_name: null,
    ip_address: null,
    install_status: null,
    environment: null,
    support_group: null,
    manufacturer: null,
    model_name: null,
    serial_number: null,
    asset_model_category: null,
  }),
  rowToPatch: (r) => ({
    name: r.name,
    ci_class: r.ci_class,
    linked_service_id: r.linked_service_id,
    asset_owner: r.asset_owner,
    asset_custodian: r.asset_custodian,
    physical_location: r.physical_location,
    logical_location: r.logical_location,
    pci_scope: r.pci_scope ?? false,
    backup: r.backup,
    primary_process: r.primary_process,
    secondary_process: r.secondary_process,
    contract_ref: r.contract_ref,
    vendor_contact: r.vendor_contact,
    vendor_sla: r.vendor_sla,
    asset_display_name: r.asset_display_name,
    ip_address: r.ip_address,
    install_status: r.install_status,
    environment: r.environment,
    support_group: r.support_group,
    manufacturer: r.manufacturer,
    model_name: r.model_name,
    serial_number: r.serial_number,
    asset_model_category: r.asset_model_category,
  }),
  extraSections: [
    makeTwinDrillSection("configuration_item", "foundation-ci-twin-drill"),
  ],
};
